import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { DriverVehicleHeatmapDashboardDto } from 'api/dashboard/dtos/DriverVehicleHeatmapDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './DriverVehicleHeatmap.module.scss';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

type TooltipState = {
	driverName: string;
	registrationNumber: string;
	count: number;
	driverTotal: number;
	x: number;
	y: number;
};

const TEAL_SCALE = ['#eef9f9', '#b8f0f0', '#6de0e0', '#02c7c7', '#038cb6', '#025a7a'];

const getCellColor = (count: number, maxCount: number): string => {
	if (count === 0) return TEAL_SCALE[0];
	const ratio = count / maxCount;
	if (ratio >= 0.8) return TEAL_SCALE[5];
	if (ratio >= 0.6) return TEAL_SCALE[4];
	if (ratio >= 0.4) return TEAL_SCALE[3];
	if (ratio >= 0.2) return TEAL_SCALE[2];
	return TEAL_SCALE[1];
};

const DriverVehicleHeatmap = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<DriverVehicleHeatmapDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('driverVehicleHeatmap');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getDriverVehicleHeatmap(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const { drivers, vehicles, matrix, maxCount } = useMemo(() => {
		const items = data || [];
		const driverMap = new Map<string, { id: string; name: string; total: number }>();
		const vehicleMap = new Map<string, { id: string; registration: string; total: number }>();
		const cell = new Map<string, number>();
		let max = 0;

		items.forEach(item => {
			if (!driverMap.has(item.driverId)) {
				driverMap.set(item.driverId, { id: item.driverId, name: item.driverName, total: item.driverTotal });
			}
			if (!vehicleMap.has(item.vehicleId)) {
				vehicleMap.set(item.vehicleId, { id: item.vehicleId, registration: item.registrationNumber, total: 0 });
			}
			const veh = vehicleMap.get(item.vehicleId)!;
			veh.total += item.reservationsCount;
			const key = `${item.driverId}_${item.vehicleId}`;
			cell.set(key, item.reservationsCount);
			if (item.reservationsCount > max) max = item.reservationsCount;
		});

		const driversArr = Array.from(driverMap.values()).sort((a, b) => b.total - a.total);
		const vehiclesArr = Array.from(vehicleMap.values()).sort((a, b) => b.total - a.total);

		return { drivers: driversArr, vehicles: vehiclesArr, matrix: cell, maxCount: max };
	}, [data]);

	const handleCellEnter = (driverName: string, registration: string, count: number, driverTotal: number, evt: React.MouseEvent) => {
		setTooltip({ driverName, registrationNumber: registration, count, driverTotal, x: evt.clientX, y: evt.clientY });
	};

	const handleCellMove = (evt: React.MouseEvent) => {
		setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
	};

	const handleCellLeave = () => setTooltip(null);

	const hasData = drivers.length > 0 && vehicles.length > 0;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.driver_vehicle_heatmap.title' as any) as string}
			subTitleTooltip={t('dashboard.driver_vehicle_heatmap.tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !hasData ? (
				<WidgetNoData />
			) : (
				<div className={styles.heatmapWrapper}>
					<table className={styles.heatmapTable}>
						<thead>
							<tr>
								<th className={styles.cornerCell}></th>
								{vehicles.map(v => (
									<th key={v.id} className={styles.vehicleHeader}>
										<span className={styles.vehicleLabel}>{v.registration}</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{drivers.map(driver => (
								<tr key={driver.id}>
									<td className={styles.driverHeader} title={driver.name}>{driver.name}</td>
									{vehicles.map(v => {
										const count = matrix.get(`${driver.id}_${v.id}`) || 0;
										const bg = count > 0 ? getCellColor(count, maxCount) : TEAL_SCALE[0];
										const textColor = count > 0 && count / maxCount >= 0.4 ? '#fff' : '#374151';
										return (
											<td
												key={v.id}
												className={styles.cell}
												style={{ background: bg, color: textColor }}
												onMouseEnter={count > 0 ? e => handleCellEnter(driver.name, v.registration, count, driver.total, e) : undefined}
												onMouseMove={count > 0 ? handleCellMove : undefined}
												onMouseLeave={count > 0 ? handleCellLeave : undefined}
											>
												{count > 0 ? count : ''}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
					<div className={styles.legend}>
						<span className={styles.legendLabel}>{t('dashboard.driver_vehicle_heatmap.legend_min' as any)}</span>
						{TEAL_SCALE.slice(1).map((c, i) => (
							<span key={i} className={styles.legendSwatch} style={{ background: c }} />
						))}
						<span className={styles.legendLabel}>{t('dashboard.driver_vehicle_heatmap.legend_max' as any)}</span>
					</div>
				</div>
			)}
			{tooltip && (
				<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
					<div className={styles.tooltipTitle}>
						{tooltip.driverName} &middot; {tooltip.registrationNumber}
					</div>
					<div className={styles.tooltipRow}>
						<span>{t('dashboard.driver_vehicle_heatmap.tooltip_count' as any)}:</span>
						<span className={styles.tooltipValue}>{tooltip.count}</span>
					</div>
					<div className={styles.tooltipRow}>
						<span>{t('dashboard.driver_vehicle_heatmap.tooltip_share' as any)}:</span>
						<span className={styles.tooltipValue}>
							{tooltip.driverTotal > 0 ? Math.round((tooltip.count / tooltip.driverTotal) * 100) : 0}%
						</span>
					</div>
				</ChartTooltipPortal>
			)}
		</Box>
	);
};

export default DriverVehicleHeatmap;
